import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faInfoCircle,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";

const ReferencesDataGrid = ({
  data,
  totalItems,
  pagination,
  sortConfig,
  onSort,
  onPageChange,
  onPageSizeChange,
  onDetailsClick,
  formatDate,
  getStatusColorClass,
  getTransportIcon,
  getOperationTypeText,
  calculateEtaCompliance,
  calculateExitTime,
  calculatePaymentTime,
  isInValidationProcess,
  isOutOfTimeValidation,
}) => {
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <FontAwesomeIcon
        icon={faChevronUp}
        className="ml-1 text-blue-500"
        size="xs"
      />
    ) : (
      <FontAwesomeIcon
        icon={faChevronDown}
        className="ml-1 text-blue-500"
        size="xs"
      />
    );
  };

  const renderKPIIndicator = (item) => {
    // ETA compliance indicator
    const etaCompliance = calculateEtaCompliance(item);
    const isEtaCompliant = etaCompliance.compliant;

    // Validation process indicator
    const inValidation = isInValidationProcess(item);
    const outOfTime = isOutOfTimeValidation(item);

    // Exit and payment time availability
    const hasExitTime = calculateExitTime(item) !== null;
    const hasPaymentTime = calculatePaymentTime(item) !== null;

    return (
      <div className="flex space-x-1">
        {/* ETA Compliance Indicator */}
        {etaCompliance.daysDifference !== null && (
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full ${
              isEtaCompliant ? "bg-green-100" : "bg-red-100"
            }`}
            title={`ETA Compliance: ${
              isEtaCompliant
                ? `Compliant (${etaCompliance.daysDifference} days)`
                : `Non-compliant (${etaCompliance.daysDifference} days)`
            }`}
          >
            <FontAwesomeIcon
              icon={isEtaCompliant ? faCheckCircle : faTimesCircle}
              className={isEtaCompliant ? "text-green-600" : "text-red-600"}
              size="xs"
            />
          </div>
        )}

        {/* Validation Process Indicator */}
        {(inValidation || outOfTime) && (
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full ${
              outOfTime ? "bg-red-100" : "bg-yellow-100"
            }`}
            title={`Validation Process: ${
              outOfTime ? "Out of time" : "In process"
            }`}
          >
            <FontAwesomeIcon
              icon={outOfTime ? faExclamationTriangle : faHourglassHalf}
              className={outOfTime ? "text-red-600" : "text-yellow-600"}
              size="xs"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white shadow-lg rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mr-2" />
          References Data
        </h3>

        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">
            Showing {data.length} of {totalItems} records
          </span>

          <button className="flex items-center space-x-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors">
            <FontAwesomeIcon icon={faFileExcel} />
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => onSort("header.reference")}
                >
                  <span>Reference</span>
                  {renderSortIcon("header.reference")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() =>
                    onSort("header.pedimento.pedimento_entry_point")
                  }
                >
                  <span>Entry Point</span>
                  {renderSortIcon("header.pedimento.pedimento_entry_point")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => onSort("header.operation_type")}
                >
                  <span>Operation</span>
                  {renderSortIcon("header.operation_type")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => onSort("header.customer_name")}
                >
                  <span>Customer</span>
                  {renderSortIcon("header.customer_name")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => onSort("header.ebookingDates.fechaETA")}
                >
                  <span>ETA Date</span>
                  {renderSortIcon("header.ebookingDates.fechaETA")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <button
                  className="flex items-center space-x-1 hover:text-blue-700 focus:outline-none"
                  onClick={() => onSort("header.trafficStatus")}
                >
                  <span>Status</span>
                  {renderSortIcon("header.trafficStatus")}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                KPIs
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                  No references found matching your criteria
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.header.reference} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="font-medium">{item.header.reference}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {item.header.pedimento?.pedimento_number || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      {item.header.pedimento && (
                        <>
                          <FontAwesomeIcon
                            icon={
                              item.header.pedimento &&
                              item.header.pedimento.pedimento_entry_point
                                ? getTransportIcon(
                                    item.header.pedimento.pedimento_entry_point
                                  ).icon
                                : getTransportIcon().icon
                            }
                            className={`${
                              item.header.pedimento &&
                              item.header.pedimento.pedimento_entry_point
                                ? getTransportIcon(
                                    item.header.pedimento.pedimento_entry_point
                                  ).color
                                : getTransportIcon().color
                            } mr-2`}
                            title={
                              item.header.pedimento &&
                              item.header.pedimento.pedimento_entry_point
                                ? getTransportIcon(
                                    item.header.pedimento.pedimento_entry_point
                                  ).title
                                : getTransportIcon().title
                            }
                          />
                          <span>
                            {item.header.pedimento.pedimento_entry_point}
                          </span>
                        </>
                      )}
                      {!item.header.pedimento && (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getOperationTypeText(item.header.operation_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="font-medium truncate max-w-xs">
                      {item.header.customer_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                      {item.header.customer_division || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(item.header.ebookingDates?.fechaETA)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.header.trafficStatus ? (
                      <div
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(
                          item.header.trafficStatus
                        )}`}
                      >
                        {item.header.trafficStatus}
                      </div>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderKPIIndicator(item)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => onDetailsClick(item)}
                      className="text-blue-600 hover:text-blue-800 transition-colors font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <select
            className="mr-2 border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
            value={pagination.pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            Showing{" "}
            {totalItems > 0
              ? (pagination.currentPage - 1) * pagination.pageSize + 1
              : 0}{" "}
            to{" "}
            {Math.min(pagination.currentPage * pagination.pageSize, totalItems)}{" "}
            of {totalItems} records
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(1)}
            disabled={
              pagination.currentPage === 1 || pagination.totalPages === 0
            }
            className={`px-2 py-1 text-sm rounded ${
              pagination.currentPage === 1 || pagination.totalPages === 0
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-50"
            }`}
          >
            First
          </button>

          <button
            onClick={() => onPageChange(pagination.currentPage - 1)}
            disabled={
              pagination.currentPage === 1 || pagination.totalPages === 0
            }
            className={`px-2 py-1 text-sm rounded ${
              pagination.currentPage === 1 || pagination.totalPages === 0
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-50"
            }`}
          >
            Previous
          </button>

          {/* Page numbers */}
          <div className="flex items-center">
            {Array.from(
              { length: Math.min(5, pagination.totalPages) },
              (_, i) => {
                // Create a window of 5 pages centered around current page
                let pageWindow = [];
                if (pagination.totalPages <= 5) {
                  // Show all pages if 5 or fewer
                  pageWindow = Array.from(
                    { length: pagination.totalPages },
                    (_, i) => i + 1
                  );
                } else {
                  // Create window around current page
                  const start = Math.max(
                    1,
                    Math.min(
                      pagination.currentPage - 2,
                      pagination.totalPages - 4
                    )
                  );
                  pageWindow = Array.from({ length: 5 }, (_, i) => start + i);
                }

                return pageWindow.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md mx-0.5 ${
                      pagination.currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                ));
              }
            )}
          </div>

          <button
            onClick={() => onPageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`px-2 py-1 text-sm rounded ${
              pagination.currentPage === pagination.totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-50"
            }`}
          >
            Next
          </button>

          <button
            onClick={() => onPageChange(pagination.totalPages)}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`px-2 py-1 text-sm rounded ${
              pagination.currentPage === pagination.totalPages
                ? "text-gray-400 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-50"
            }`}
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferencesDataGrid;
